// Every segment here is scroll-driven and uses both width and height tiers, so
// check a change at each tier: 375×667 (iPhone SE, which the non-overlapping
// short-viewport rules must target explicitly), 390×844, 1280×720, 1366×768,
// 1920×1080, 2560×1440, and 3840×2160.
//
// Tailwind v4 orders arbitrary `[@media(...)]:` variants by its own sort, not by
// their order in the class string, so overlapping rules can let the wrong one
// win. Write height and width ranges that never overlap, as `HeroTextPanel` and
// `BEAT_FOUR_VISUAL_SHELL` do.

import React from 'react';
import SEO from '../../seo/SEO';
import BalloonField from '@/components/BalloonField';
import HeroTextPanel from './HeroTextPanel';
import SplashVideo from './home/SplashVideo';
import {
  useHomeColorSync,
  useSystemScrollVh,
  useSystemWindowSize,
} from '../../hooks';
import {
  BeatFourVisualGroup,
  BeatFiveDeviceNetwork,
  BeatSixPyramid,
  BeatSevenSupportArc,
  BeatEightArcadePricing,
  BeatNineCommunities,
  GameCoverFilmstrip,
  ScrollAvatar,
  ScrollBackdrop,
  ScrollContent,
} from './home/index';
import type {
  MotionStop,
  ScrollSegment,
  ScrollAvatarSegment,
  ScrollBackdropSegment,
  ScrollContentSegment,
} from '../../types/homeScroll';
import { ASSETS, VIDEOS } from '@airhead-games/shared/configs';
import './home/ScrollShell.css';

const getSegmentEndVh = ({ startVh, durationVh }: ScrollSegment): number =>
  startVh + durationVh;

const BACKDROP_SEGMENTS: ScrollBackdropSegment[] = [
  {
    id: 'backdrop-red',
    startVh: 25,
    durationVh: 100,
    colorFamily: 'red',
    headerAccentLagVh: 14,
  },
  {
    id: 'backdrop-orange',
    startVh: 100,
    durationVh: 100,
    colorFamily: 'orange',
    headerAccentLagVh: 3,
  },
  {
    id: 'backdrop-amber',
    startVh: 175,
    durationVh: 100,
    colorFamily: 'amber',
    headerAccentLagVh: 3,
  },
  {
    id: 'backdrop-green',
    startVh: 250,
    durationVh: 100,
    colorFamily: 'green',
    headerAccentLagVh: 3,
  },
  {
    id: 'backdrop-teal',
    startVh: 325,
    durationVh: 100,
    colorFamily: 'teal',
    headerAccentLagVh: 3,
  },
  {
    id: 'backdrop-blue',
    startVh: 400,
    durationVh: 100,
    colorFamily: 'blue',
    headerAccentLagVh: 3,
  },
  {
    id: 'backdrop-purple',
    startVh: 475,
    durationVh: 100,
    colorFamily: 'purple',
    headerAccentLagVh: 3,
  },
  {
    id: 'backdrop-pink',
    startVh: 550,
    durationVh: 80,
    colorFamily: 'pink',
    headerAccentLagVh: 3,
  },
];

const AVATAR_SEGMENTS: ScrollAvatarSegment[] = [
  {
    id: 'avatar-red',
    startVh: 0,
    durationVh: 100,
    colorFamily: 'red',
    eyeShape: 'ghost',
    mouthShape: 'watermelon',
  },
  {
    id: 'avatar-orange',
    startVh: 75,
    durationVh: 100,
    colorFamily: 'orange',
    eyeShape: 'boomerang',
    mouthShape: 'boat',
  },
  {
    id: 'avatar-amber',
    startVh: 150,
    durationVh: 100,
    colorFamily: 'amber',
    eyeShape: 'sunset',
    mouthShape: 'peanut',
  },
  {
    id: 'avatar-green',
    startVh: 225,
    durationVh: 100,
    colorFamily: 'green',
    eyeShape: 'bookmark',
    mouthShape: 'crescent',
  },
  {
    id: 'avatar-teal',
    startVh: 300,
    durationVh: 100,
    colorFamily: 'teal',
    eyeShape: 'bean',
    mouthShape: 'falcon',
  },
  {
    id: 'avatar-blue',
    startVh: 375,
    durationVh: 100,
    colorFamily: 'blue',
    eyeShape: 'boomerang',
    mouthShape: 'crescent',
  },
  {
    id: 'avatar-purple',
    startVh: 460,
    durationVh: 90,
    colorFamily: 'purple',
    eyeShape: 'fruitslice',
    mouthShape: 'heart',
  },
  {
    id: 'avatar-pink',
    startVh: 535,
    durationVh: 100,
    colorFamily: 'pink',
    eyeShape: 'bandaid',
    mouthShape: 'muffin',
  },
];

/** Centered max-width shell used by every hero-style ScrollContent segment.
 *
 *  Anchors `left` / `right` to `--hero-bound-gutter` (defined in
 *  `styles/tailwind.css`) so segments grow with the viewport up to
 *  `--hero-bound-max`, then sit centered with growing outer gutters on wider
 *  viewports. This keeps the composition's elements grouped together on QHD/
 *  4K displays instead of drifting toward viewport edges.
 *
 *  `top` offsets remain per-breakpoint so segments still anchor relative to
 *  the fixed header on mobile / tablet; the `lg+` variants use
 *  `var(--app-header-h)` to reserve header space for vertically-centered
 *  pairs.
 */
const HERO_TEXT_PANEL_SHELL =
  'top-28 sm:top-32 md:top-36 ' +
  'left-[var(--hero-bound-gutter)] right-[var(--hero-bound-gutter)]';

/** Beat 1 hero copy: top-anchored on < lg (matches HERO_TEXT_PANEL_SHELL);
 *  on lg+ spans the left portion of the bounded shell and is vertically
 *  centered between the header and viewport bottom. The right inset reserves
 *  `var(--hero-video-reserve)` for the splash video plus a 2rem gap,
 *  keeping the pair grouped together regardless of viewport width. */
const BEAT_ONE_TEXT_SHELL =
  'top-28 sm:top-32 md:top-36 ' +
  'left-[var(--hero-bound-gutter)] right-[var(--hero-bound-gutter)] ' +
  'lg:top-[var(--app-header-h)] lg:bottom-0 ' +
  'lg:right-[calc(var(--hero-bound-gutter)+var(--hero-video-reserve)+2rem)] ' +
  'lg:flex lg:items-center';

/** Beat 1 splash video: on < lg the segment's `top` and `bottom` are set
 *  inline to bracket the region between the hero copy and the viewport bottom,
 *  and `items-center` vertically centers the video inside that region (so it
 *  doesn't visually crowd the copy when the region is taller than the video).
 *  On lg+ the segment occupies a `var(--hero-video-reserve)`-wide column on
 *  the right side of the bounded shell, vertically centered below the
 *  header. */
const BEAT_ONE_VIDEO_SHELL =
  'left-0 right-0 flex items-center justify-center px-6 sm:px-8 md:px-10 ' +
  'lg:top-[var(--app-header-h)] lg:bottom-0 ' +
  'lg:left-[calc(100%-var(--hero-bound-gutter)-var(--hero-video-reserve))] ' +
  'lg:right-[var(--hero-bound-gutter)] lg:px-0';

const BEAT_ONE_HERO_TOP_PX = 112; // top-28 (Tailwind 7rem at 16px base)
const BEAT_ONE_GAP_PX = 24;
const BEAT_ONE_BOTTOM_MARGIN_PX = 40;
const BEAT_ONE_SIDE_GUTTER_PX = 24;
const BEAT_ONE_VIDEO_MAX_WIDTH_PX = 360;
const BEAT_ONE_VIDEO_MIN_HEIGHT_PX = 160;
const BEAT_ONE_LG_BREAKPOINT_PX = 1024;
const HOME_SCROLL_NUDGE_START_DELAY_MS = 6500;
const HOME_SCROLL_NUDGE_DOWN_MS = 1200;
const HOME_SCROLL_NUDGE_UP_MS = 1200;
const HOME_SCROLL_NUDGE_PAUSE_BETWEEN_CYCLES_MS = 1000;
const HOME_SCROLL_NUDGE_DISTANCE_PX = 96;
const HOME_SCROLL_NUDGE_MAX_CYCLES = 5;
const HOME_SCROLL_NUDGE_STOP_SCROLL_Y_PX = 80;
const BEAT_ONE_OPACITY_TRACK: MotionStop[] = [
  [0, 1],
  [70, 1],
  [100, 0, 'easeOut'],
];
const BEAT_ONE_Y_TRACK: MotionStop[] = [
  [0, 0],
  [70, 0],
  [100, -28, 'easeOut'],
];
const BEAT_ONE_TRACKS: ScrollContentSegment['tracks'] = {
  opacity: BEAT_ONE_OPACITY_TRACK,
  yPx: BEAT_ONE_Y_TRACK,
};

/** Beat 4 shell: hosts the combined doc quote image + mad-lib card. The
 *  wrapper inside locks to the doc-meme image aspect ratio, and
 *  `items-center` centers it vertically in the available below-hero region
 *  so it doesn't get locked to the viewport edge on tall mobile devices.
 *
 *  Top offset grows up the breakpoint ladder to clear the Beat-4 hero text,
 *  which itself grows with viewport width. Short-viewport overrides claw
 *  the offset back so the visual still has room to render on 768px-tall
 *  laptops and iPhone-SE-class devices. */
const BEAT_FOUR_VISUAL_SHELL = [
  'top-[16.5rem] bottom-6 left-0 right-0 flex items-center justify-center px-3',
  'sm:top-[17.5rem] sm:bottom-8 sm:px-4',
  'md:top-[17rem] md:bottom-10 md:px-6',
  'lg:top-[19rem] lg:bottom-10 lg:px-10',
  'xl:top-[21.5rem] xl:bottom-10 xl:px-14',
  '2xl:top-[26rem] 2xl:bottom-12 2xl:px-20',
  'min-[1920px]:top-[34rem] min-[1920px]:bottom-14',
  'min-[2560px]:top-[42rem] min-[2560px]:bottom-16',
  'min-[3840px]:top-[54rem] min-[3840px]:bottom-20',
  // Mutually-exclusive short-viewport overrides. Tailwind v4 sorts arbitrary
  // media queries by its own algorithm (not source order), so overlapping
  // rules (e.g. a bare `max-h-720` + a bare `max-h-820`) produce
  // unpredictable cascade winners. Each rule below targets exactly one
  // height × width range so cascade ordering is irrelevant.
  // — Short (≤720px tall):
  '[@media(max-height:720px)_and_(max-width:1023px)]:top-[12rem]',
  '[@media(max-height:720px)_and_(max-width:1023px)]:bottom-6',
  '[@media(max-height:720px)_and_(min-width:1024px)]:top-[16rem]',
  '[@media(max-height:720px)_and_(min-width:1024px)]:bottom-8',
  // — Medium-short (721–820px tall):
  '[@media(min-height:721px)_and_(max-height:820px)_and_(max-width:1023px)]:top-[14rem]',
  '[@media(min-height:721px)_and_(max-height:820px)_and_(max-width:1023px)]:bottom-6',
  '[@media(min-height:721px)_and_(max-height:820px)_and_(min-width:1024px)]:top-[20rem]',
  '[@media(min-height:721px)_and_(max-height:820px)_and_(min-width:1024px)]:bottom-8',
].join(' ');

/** Beat 6 shell: hosts the streaming-platforms pyramid. Beat 6 has nothing
 *  tall above it (just the hero text panel), so the top inset is much
 *  smaller than `BEAT_FOUR_VISUAL_SHELL`. Same short-viewport override
 *  pattern (mutually exclusive height × width ranges) so the pyramid stays
 *  on-viewport at 1366×768, 1280×720, and similar short-laptop sizes. */
const BEAT_SIX_VISUAL_SHELL = [
  'top-[17.5rem] bottom-6 left-0 right-0 flex items-center justify-center px-3',
  'sm:top-[18.5rem] sm:bottom-8 sm:px-4',
  'md:top-[19rem] md:bottom-10 md:px-6',
  'lg:top-[19rem] lg:bottom-10 lg:px-10',
  'xl:top-[20.5rem] xl:bottom-10 xl:px-14',
  '2xl:top-[22rem] 2xl:bottom-12 2xl:px-20',
  'min-[1920px]:top-[22rem] min-[1920px]:bottom-14',
  'min-[2560px]:top-[26rem] min-[2560px]:bottom-16',
  'min-[3840px]:top-[32rem] min-[3840px]:bottom-20',
  // Short-viewport overrides — see BEAT_FOUR_VISUAL_SHELL for the
  // mutual-exclusion rationale.
  '[@media(max-height:720px)_and_(max-width:1023px)]:top-[12.5rem]',
  '[@media(max-height:720px)_and_(max-width:1023px)]:bottom-6',
  '[@media(max-height:720px)_and_(min-width:1024px)]:top-[15rem]',
  '[@media(max-height:720px)_and_(min-width:1024px)]:bottom-8',
  '[@media(min-height:721px)_and_(max-height:820px)_and_(max-width:1023px)]:top-[14rem]',
  '[@media(min-height:721px)_and_(max-height:820px)_and_(max-width:1023px)]:bottom-6',
  '[@media(min-height:721px)_and_(max-height:820px)_and_(min-width:1024px)]:top-[17rem]',
  '[@media(min-height:721px)_and_(max-height:820px)_and_(min-width:1024px)]:bottom-8',
].join(' ');

/** Beat 7 shell: the support wheel is a full-bleed composition (a giant
 *  circle whose top arc spans the entire viewport and overflows the edges),
 *  so unlike the bounded Beat 6 pyramid it covers the whole sticky viewport.
 *  The component sizes its own geometry to the live viewport; the hero text
 *  panel renders above it (higher zIndex). */
const BEAT_SEVEN_VISUAL_SHELL = 'inset-0';

/** Beat 8 shell: the arcade-pricing composition (cabinet anchored to the
 *  bottom of the viewport + Loon coins falling from the top). The component
 *  positions its own pieces against the sticky viewport, so the shell is
 *  full-bleed like Beat 7. */
const BEAT_EIGHT_VISUAL_SHELL = 'inset-0';

/** Beat 9 shell: hosts the scrapbook-style polaroid triptych (off-stream
 *  communities). Same top-inset / short-viewport pattern as Beat 6 so the
 *  triptych clears the hero text on all breakpoints without crowding short
 *  laptop viewports. */
const BEAT_NINE_VISUAL_SHELL = [
  'top-[17.5rem] bottom-6 left-0 right-0 flex items-center justify-center px-3',
  'sm:top-[18.5rem] sm:bottom-8 sm:px-4',
  'md:top-[19rem] md:bottom-10 md:px-6',
  'lg:top-[19rem] lg:bottom-10 lg:px-10',
  'xl:top-[20.5rem] xl:bottom-10 xl:px-14',
  '2xl:top-[22rem] 2xl:bottom-12 2xl:px-20',
  // Hero title scales up to text-9xl (128px) at min-[1920px] and
  // text-[10rem]/text-[12rem] beyond, AND wraps to two lines because
  // "Bring together any community" exceeds the title max-width at
  // these breakpoints. Hero-panel bottom lands well past 22rem; these
  // top insets keep the polaroid trio with clear breathing room below.
  'min-[1920px]:top-[36rem] min-[1920px]:bottom-14',
  'min-[2560px]:top-[40rem] min-[2560px]:bottom-16',
  'min-[3840px]:top-[48rem] min-[3840px]:bottom-20',
  // Short-viewport overrides — see BEAT_FOUR_VISUAL_SHELL for the
  // mutual-exclusion rationale.
  '[@media(max-height:720px)_and_(max-width:1023px)]:top-[12.5rem]',
  '[@media(max-height:720px)_and_(max-width:1023px)]:bottom-6',
  '[@media(max-height:720px)_and_(min-width:1024px)]:top-[15rem]',
  '[@media(max-height:720px)_and_(min-width:1024px)]:bottom-8',
  '[@media(min-height:721px)_and_(max-height:820px)_and_(max-width:1023px)]:top-[14rem]',
  '[@media(min-height:721px)_and_(max-height:820px)_and_(max-width:1023px)]:bottom-6',
  '[@media(min-height:721px)_and_(max-height:820px)_and_(min-width:1024px)]:top-[17rem]',
  '[@media(min-height:721px)_and_(max-height:820px)_and_(min-width:1024px)]:bottom-8',
].join(' ');

const CONTENT_SEGMENTS: ScrollContentSegment[] = [
  {
    id: 'beat-2-social-proof-text',
    startVh: 45,
    durationVh: 60,
    shellClassName: HERO_TEXT_PANEL_SHELL,
    zIndex: 5,
    tracks: {
      opacity: [
        [0, 0],
        [15, 1],
        [75, 1],
        [100, 0, 'easeOut'],
      ],
      yPx: [
        [0, 32],
        [15, 0],
        [75, 0],
        [100, -28, 'easeOut'],
      ],
    },
    children: (
      <HeroTextPanel
        testimonial
        title={'\u201cI have not laughed so much in years!\u201d'}
        body="@Edgywizard"
        bodyHref="https://www.twitch.tv/edgywizard"
        palette="red"
      />
    ),
  },
  {
    id: 'beat-3-variety-panel',
    startVh: 115,
    durationVh: 75,
    shellClassName: HERO_TEXT_PANEL_SHELL,
    zIndex: 5,
    tracks: {
      opacity: [
        [0, 0],
        [18, 1],
        [82, 1],
        [100, 0, 'easeOut'],
      ],
      yPx: [
        [0, 32],
        [18, 0],
        [82, 0],
        [100, -28, 'easeOut'],
      ],
    },
    children: (
      <HeroTextPanel
        title="Our game library keeps growing"
        body="Wordplay, deduction, and puzzles. Free-for-all, co-op, and team. New games drop often."
        palette="orange"
      />
    ),
  },
];

/** Filmstrip entrance/exit buffer (px) — keeps the strip a comfortable
 *  distance offscreen at the start/end of its scroll segment so it never
 *  peeks in early on large viewports. */
const FILMSTRIP_OFFSCREEN_BUFFER_PX = 120;
/** Fallback intrinsic width used before the filmstrip element is measured
 *  (or if measurement is unavailable). Comfortably wider than the largest
 *  expected QHD/4K layout. */
const FILMSTRIP_FALLBACK_WIDTH_PX = 4800;

const HOME_SCROLL_TIMELINE_VH = Math.max(
  ...BACKDROP_SEGMENTS.map(getSegmentEndVh),
  ...AVATAR_SEGMENTS.map(getSegmentEndVh),
  ...CONTENT_SEGMENTS.map(getSegmentEndVh),
  252 + 70
);

const animateElementScrollTop = ({
  element,
  from,
  to,
  durationMs,
  shouldStop,
}: {
  element: HTMLElement;
  from: number;
  to: number;
  durationMs: number;
  shouldStop: () => boolean;
}): Promise<void> =>
  new Promise((resolve) => {
    if (durationMs <= 0 || from === to || shouldStop()) {
      resolve();
      return;
    }

    const startAt = performance.now();
    const delta = to - from;
    const easeInOut = (t: number): number => 0.5 - 0.5 * Math.cos(Math.PI * t);

    const step = (now: number) => {
      if (shouldStop()) {
        resolve();
        return;
      }

      const elapsed = now - startAt;
      const progress = Math.min(elapsed / durationMs, 1);
      const eased = easeInOut(progress);
      element.scrollTop = from + delta * eased;

      if (progress >= 1) {
        resolve();
        return;
      }

      window.requestAnimationFrame(step);
    };

    window.requestAnimationFrame(step);
  });

const Home: React.FC = () => {
  const { pageRef, stageRef, scrollVh, stageHeightVh } = useSystemScrollVh(
    HOME_SCROLL_TIMELINE_VH
  );
  const { width: windowWidth, height: windowHeight } = useSystemWindowSize(50);

  const heroTextRef = React.useRef<HTMLDivElement | null>(null);
  const [heroTextHeightPx, setHeroTextHeightPx] = React.useState<number | null>(
    null
  );

  React.useEffect(() => {
    const element = heroTextRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      setHeroTextHeightPx(entry.contentRect.height);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const filmstripRef = React.useRef<HTMLDivElement | null>(null);
  const [filmstripWidthPx, setFilmstripWidthPx] = React.useState<number>(
    FILMSTRIP_FALLBACK_WIDTH_PX
  );

  React.useEffect(() => {
    const element = filmstripRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const measured = entry.contentRect.width;
      if (measured > 0) setFilmstripWidthPx(measured);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const pageElement = pageRef.current;
    if (!pageElement) return;
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );
    if (prefersReducedMotion.matches) return;
    if (pageElement.scrollTop > HOME_SCROLL_NUDGE_STOP_SCROLL_Y_PX) return;

    let cancelled = false;
    let userHasTakenControl = false;
    let timeoutId: number | null = null;
    const shouldStop = () => cancelled || userHasTakenControl;
    const markUserControl = () => {
      userHasTakenControl = true;
    };

    pageElement.addEventListener('wheel', markUserControl, { passive: true });
    pageElement.addEventListener('touchstart', markUserControl, {
      passive: true,
    });
    window.addEventListener('keydown', markUserControl);
    pageElement.addEventListener('pointerdown', markUserControl, {
      passive: true,
    });

    const run = async () => {
      timeoutId = window.setTimeout(async () => {
        if (shouldStop()) return;

        let cyclesCompleted = 0;

        while (
          !shouldStop() &&
          cyclesCompleted < HOME_SCROLL_NUDGE_MAX_CYCLES
        ) {
          const currentY = pageElement.scrollTop;
          const nextY = currentY + HOME_SCROLL_NUDGE_DISTANCE_PX;

          await animateElementScrollTop({
            element: pageElement,
            from: currentY,
            to: nextY,
            durationMs: HOME_SCROLL_NUDGE_DOWN_MS,
            shouldStop,
          });
          if (shouldStop()) break;

          await animateElementScrollTop({
            element: pageElement,
            from: pageElement.scrollTop,
            to: currentY,
            durationMs: HOME_SCROLL_NUDGE_UP_MS,
            shouldStop,
          });
          if (shouldStop()) break;

          cyclesCompleted += 1;

          if (cyclesCompleted >= HOME_SCROLL_NUDGE_MAX_CYCLES || shouldStop())
            break;

          await new Promise<void>((resolve) => {
            timeoutId = window.setTimeout(
              resolve,
              HOME_SCROLL_NUDGE_PAUSE_BETWEEN_CYCLES_MS
            );
          });
        }
      }, HOME_SCROLL_NUDGE_START_DELAY_MS);
    };

    void run();

    return () => {
      cancelled = true;
      if (timeoutId !== null) window.clearTimeout(timeoutId);
      pageElement.removeEventListener('wheel', markUserControl);
      pageElement.removeEventListener('touchstart', markUserControl);
      window.removeEventListener('keydown', markUserControl);
      pageElement.removeEventListener('pointerdown', markUserControl);
    };
  }, [pageRef]);

  const isLg = windowWidth >= BEAT_ONE_LG_BREAKPOINT_PX;

  /** Compute the splash-video position + dimensions to fit the space below the
   *  hero copy on small/tablet widths. Returns null on lg+ (static layout) or
   *  before the first measurement (segment renders empty until then). */
  const mobileVideoLayout = React.useMemo(() => {
    if (
      isLg ||
      heroTextHeightPx == null ||
      windowHeight === 0 ||
      windowWidth === 0
    ) {
      return null;
    }
    const availableHeight =
      windowHeight -
      BEAT_ONE_HERO_TOP_PX -
      heroTextHeightPx -
      BEAT_ONE_GAP_PX -
      BEAT_ONE_BOTTOM_MARGIN_PX;
    if (availableHeight < BEAT_ONE_VIDEO_MIN_HEIGHT_PX) return null;
    const availableWidth = windowWidth - 2 * BEAT_ONE_SIDE_GUTTER_PX;
    const aspect = VIDEOS.splashHome.aspectRatio;
    const heightDrivenWidth = availableHeight * aspect;
    const width = Math.min(
      heightDrivenWidth,
      availableWidth,
      BEAT_ONE_VIDEO_MAX_WIDTH_PX
    );
    const height = width / aspect;
    const topPx = BEAT_ONE_HERO_TOP_PX + heroTextHeightPx + BEAT_ONE_GAP_PX;
    const bottomPx = BEAT_ONE_BOTTOM_MARGIN_PX;
    return { width, height, topPx, bottomPx };
  }, [heroTextHeightPx, isLg, windowHeight, windowWidth]);

  const beatOneHeroTextSegment: ScrollContentSegment = {
    id: 'beat-1-hero-text',
    startVh: 0,
    durationVh: 50,
    shellClassName: BEAT_ONE_TEXT_SHELL,
    tracks: BEAT_ONE_TRACKS,
    children: (
      <div ref={heroTextRef}>
        <HeroTextPanel
          eyebrow="Party games for streamers"
          title="Build a community worth coming back to"
          body="Games shaped by the people who play them. Fresh every session, frictionless to join."
        />
      </div>
    ),
  };

  const beatOneSplashVideoSegment: ScrollContentSegment = {
    id: 'beat-1-splash-video',
    startVh: 0,
    durationVh: 50,
    shellClassName: BEAT_ONE_VIDEO_SHELL,
    zIndex: 2,
    tracks: BEAT_ONE_TRACKS,
    style: mobileVideoLayout
      ? { top: mobileVideoLayout.topPx, bottom: mobileVideoLayout.bottomPx }
      : undefined,
    children: isLg ? (
      <SplashVideo
        entry={VIDEOS.splashHome}
        className={[
          'mx-auto w-full',
          // Width ladder grows past xl so the video keeps pace on QHD/4K displays.
          'max-w-[260px] xl:max-w-[320px] 2xl:max-w-[400px]',
          'min-[1920px]:max-w-[500px] min-[2560px]:max-w-[620px]',
          // Height-aware caps for short laptop viewports (the video is 9:16
          // portrait, so a 320px width = 569px tall — too tall for 720px-tall
          // viewports once the header is reserved).
          '[@media(max-height:820px)]:max-w-[220px]',
          '[@media(max-height:720px)]:max-w-[180px]',
        ].join(' ')}
        rounded
      />
    ) : mobileVideoLayout ? (
      <div
        className="mx-auto"
        style={{
          width: mobileVideoLayout.width,
          height: mobileVideoLayout.height,
        }}
      >
        <SplashVideo
          entry={VIDEOS.splashHome}
          className="h-full w-full"
          rounded
        />
      </div>
    ) : null,
  };

  /** Filmstrip segment: built inside the component so its `xPx` entry/exit
   *  offsets derive from the live viewport width and measured filmstrip
   *  width. Literal px offsets leak the strip onscreen at QHD/4K start
   *  positions and don't fully scroll it off on very wide viewports. The
   *  module-level `CONTENT_SEGMENTS` can't read the live viewport size, so
   *  any segment that crosses the viewport is built here. */
  const filmstripStartPx =
    Math.max(windowWidth, 1024) + FILMSTRIP_OFFSCREEN_BUFFER_PX;
  const filmstripEndPx = -(filmstripWidthPx + FILMSTRIP_OFFSCREEN_BUFFER_PX);

  /** Edgywizard portrait slide-in distance. Anchored `bottom-0` and capped
   *  at 85svh tall, so a viewport-height-derived translateY is required to
   *  keep its top below the viewport edge before/after the segment is
   *  active. A literal px offset leaves the image visible at the top of the
   *  viewport on QHD/4K. */
  const edgywizardEntryPx = Math.max(windowHeight, 800) + 240;
  const beatThreeFilmstripSegment: ScrollContentSegment = {
    id: 'beat-3-variety-covers',
    startVh: 105,
    durationVh: 75,
    shellClassName: [
      // Top offset clears the variety hero text on each width tier. The
      // panel's title grows up the breakpoint ladder (capped per
      // `HeroTextPanel`) so the strip's top must grow alongside. Heights
      // grow up to QHD/4K so the strip's covers don't get visually buried.
      'top-[19.5rem] bottom-4 left-0 w-max',
      'sm:top-[21rem] sm:bottom-5',
      'md:top-[23rem] md:bottom-5',
      'lg:top-[25rem] lg:bottom-6',
      'xl:top-[28rem] xl:bottom-6',
      '2xl:top-[32rem] 2xl:bottom-8',
      'min-[1920px]:top-[36rem] min-[1920px]:bottom-10',
      'min-[2560px]:top-[44rem] min-[2560px]:bottom-12',
      'min-[3840px]:top-[54rem] min-[3840px]:bottom-16',
      // Short-viewport caps: 1366×768 / 1280×720 / iPhone SE etc. have so
      // little vertical room that the full filmstrip needs a higher anchor
      // (paired with smaller covers via `GameCoverFilmstrip` short-viewport
      // overrides).
      '[@media(max-height:820px)]:top-[17rem] [@media(max-height:820px)]:bottom-3',
      '[@media(max-height:820px)_and_(min-width:1024px)]:top-[19rem]',
      '[@media(max-height:720px)]:top-[14rem] [@media(max-height:720px)]:bottom-2',
      '[@media(max-height:720px)_and_(min-width:1024px)]:top-[17rem]',
    ].join(' '),
    zIndex: 4,
    tracks: {
      xPx: [
        [0, filmstripStartPx],
        [100, filmstripEndPx],
      ],
    },
    children: <GameCoverFilmstrip innerRef={filmstripRef} />,
  };

  /** Edgywizard portrait: built inside the component so the slide-in /
   *  slide-out `yPx` derives from `windowHeight + buffer`. Anchored
   *  bottom-right; width caps grow up the breakpoint ladder while height
   *  caps keep the portrait inside the available viewport area. */
  const beatTwoEdgywizardSegment: ScrollContentSegment = {
    id: 'beat-2-social-proof-avatar',
    startVh: 50,
    durationVh: 60,
    shellClassName: [
      'bottom-0 right-0 w-full',
      'max-w-[720px] sm:max-w-[780px]',
      'md:w-[58%] md:max-w-[720px] lg:max-w-[820px]',
      'xl:max-w-[920px] 2xl:max-w-[1040px]',
      'min-[1920px]:max-w-[1200px] min-[2560px]:max-w-[1480px]',
      'max-h-[85svh]',
      '[@media(max-height:820px)]:max-h-[70svh]',
      '[@media(max-height:720px)]:max-h-[60svh]',
    ].join(' '),
    zIndex: 4,
    tracks: {
      opacity: [
        [0, 1],
        [80, 1],
        [100, 0.5, 'easeOut'],
      ],
      yPx: [
        [0, edgywizardEntryPx, 'easeOut'],
        [15, 0],
        [80, 0],
        [100, edgywizardEntryPx, 'easeOut'],
      ],
    },
    children: (
      <img
        src={ASSETS.edgywizardPortrait}
        alt="Edgywizard on Twitch"
        className="block h-auto max-h-full w-full object-contain object-bottom"
        loading="lazy"
      />
    ),
  };

  const contentSegments: ScrollContentSegment[] = [
    beatOneHeroTextSegment,
    beatOneSplashVideoSegment,
    ...CONTENT_SEGMENTS,
    beatTwoEdgywizardSegment,
    beatThreeFilmstripSegment,
    {
      id: 'beat-4-hero-1',
      startVh: 185,
      durationVh: 42,
      shellClassName: HERO_TEXT_PANEL_SHELL,
      zIndex: 5,
      tracks: {
        opacity: [
          [0, 0],
          [10, 1],
          [75, 1],
          [100, 0, 'easeOut'],
        ],
        yPx: [
          [0, 32],
          [10, 0],
          [75, 0],
          [100, -28, 'easeOut'],
        ],
      },
      children: (
        <HeroTextPanel
          title="Every turn is freshly generated"
          body="No stale content or pre-packaged jokes. Humor only capped by your creativity."
          palette="amber"
        />
      ),
    },
    {
      id: 'beat-4-hero-2',
      startVh: 222,
      durationVh: 28,
      shellClassName: HERO_TEXT_PANEL_SHELL,
      zIndex: 5,
      tracks: {
        opacity: [
          [0, 0],
          [12, 1],
          [75, 1],
          [100, 0, 'easeOut'],
        ],
        yPx: [
          [0, 32],
          [12, 0],
          [75, 0],
          [100, -28, 'easeOut'],
        ],
      },
      children: (
        <HeroTextPanel
          title="Always uniquely yours"
          body="Reveals and reactions tailored to your group"
          palette="amber"
        />
      ),
    },
    {
      id: 'beat-4-visual-group',
      startVh: 180,
      durationVh: 75,
      shellClassName: BEAT_FOUR_VISUAL_SHELL,
      zIndex: 5,
      tracks: {
        opacity: [
          [0, 0],
          [8, 1],
          [92, 1],
          [100, 0, 'easeOut'],
        ],
      },
      children: <BeatFourVisualGroup scrollVh={scrollVh} />,
    },
    {
      id: 'beat-5-frictionless-text',
      startVh: 255,
      durationVh: 75,
      shellClassName: HERO_TEXT_PANEL_SHELL,
      zIndex: 5,
      tracks: {
        opacity: [
          [0, 0],
          [12, 1],
          [80, 1],
          [100, 0, 'easeOut'],
        ],
        yPx: [
          [0, 32],
          [12, 0],
          [80, 0],
          [100, -28, 'easeOut'],
        ],
      },
      children: (
        <HeroTextPanel
          title="Frictionless to join"
          body="Play from any browser in one click. No player accounts or downloads."
          palette="green"
        />
      ),
    },
    {
      id: 'beat-5-device-network',
      startVh: 265,
      durationVh: 65,
      shellClassName: BEAT_FOUR_VISUAL_SHELL,
      zIndex: 4,
      tracks: {
        opacity: [
          [0, 0],
          [10, 1],
          [80, 1],
          [100, 0, 'easeOut'],
        ],
        yPx: [
          [0, 32],
          [10, 0],
          [80, 0],
          [100, -28, 'easeOut'],
        ],
      },
      children: <BeatFiveDeviceNetwork />,
    },
    {
      id: 'beat-6-built-for-stream-text',
      startVh: 330,
      durationVh: 75,
      shellClassName: HERO_TEXT_PANEL_SHELL,
      zIndex: 5,
      tracks: {
        opacity: [
          [0, 0],
          [12, 1],
          [80, 1],
          [100, 0, 'easeOut'],
        ],
        yPx: [
          [0, 32],
          [12, 0],
          [80, 0],
          [100, -28, 'easeOut'],
        ],
      },
      children: (
        <HeroTextPanel
          title="Built for streaming"
          body="The building blocks you need to host on any platform."
          palette="teal"
        />
      ),
    },
    {
      id: 'beat-6-pyramid',
      startVh: 340,
      durationVh: 75,
      shellClassName: BEAT_SIX_VISUAL_SHELL,
      zIndex: 4,
      // Each brick choreographs its own entrance/exit (see BeatSixPyramid),
      // but the shell still needs a guaranteed visibility envelope. Without
      // it the wrapper sits at opacity 1 forever: any residual per-brick
      // opacity (a dropped final frame, or a scroll that rests inside the
      // exit ramp) bleeds over later beats, and the full-rect shell keeps
      // trapping pointer events (see ScrollContent pointerEvents note).
      // The envelope holds 1 across the whole brick window (local 0% first
      // base enter → 83.5% last base exit) then hard-cuts to 0, so it never
      // alters the choreography — it only floors the composite to exactly 0
      // once the pyramid is done. Keep every brick's exitEndPct <= 84.
      tracks: {
        opacity: [
          [0, 1],
          [84, 1],
          [84.5, 0],
        ],
      },
      children: (
        <BeatSixPyramid
          scrollVh={scrollVh}
          segmentStartVh={340}
          segmentDurationVh={75}
        />
      ),
    },
    {
      id: 'beat-7-partnership-text',
      startVh: 405,
      durationVh: 75,
      shellClassName: HERO_TEXT_PANEL_SHELL,
      zIndex: 5,
      tracks: {
        opacity: [
          [0, 0],
          [12, 1],
          [80, 1],
          [100, 0, 'easeOut'],
        ],
        yPx: [
          [0, 32],
          [12, 0],
          [80, 0],
          [100, -28, 'easeOut'],
        ],
      },
      children: (
        <HeroTextPanel
          title="Invested in your success"
          body="We are there for you before, during, and after your stream."
          palette="blue"
        />
      ),
    },
    {
      id: 'beat-7-support-arc',
      // Locked to the beat-7 hero text segment (405 / 75) so the arc draws,
      // sweeps, and exits in lockstep with the hero panel's fade.
      startVh: 405,
      durationVh: 75,
      shellClassName: BEAT_SEVEN_VISUAL_SHELL,
      zIndex: 4,
      // No segment-level opacity track — the arc, nodes, and captions handle
      // their own entrance, focus sweep, and exit (see BeatSevenSupportArc).
      children: (
        <BeatSevenSupportArc
          scrollVh={scrollVh}
          segmentStartVh={405}
          segmentDurationVh={75}
        />
      ),
    },
    {
      id: 'beat-8-arcade-pricing-text',
      startVh: 480,
      durationVh: 75,
      shellClassName: HERO_TEXT_PANEL_SHELL,
      zIndex: 5,
      tracks: {
        opacity: [
          [0, 0],
          [12, 1],
          [80, 1],
          [100, 0, 'easeOut'],
        ],
        yPx: [
          [0, 32],
          [12, 0],
          [80, 0],
          [100, -28, 'easeOut'],
        ],
      },
      children: (
        <HeroTextPanel
          title="Start for free"
          body="Your first games are on us. Affordable and predictable pricing with no commitments or expirations."
          palette="purple"
        />
      ),
    },
    {
      id: 'beat-8-arcade-cabinet',
      // Locked to the beat-8 hero text segment so the cabinet rises,
      // collects coins, and exits in lockstep with the hero panel's fade.
      startVh: 480,
      durationVh: 75,
      shellClassName: BEAT_EIGHT_VISUAL_SHELL,
      zIndex: 4,
      // No segment-level opacity track — the cabinet, coins, and screen
      // covers handle their own entrance, cycle choreography, and exit
      // (see BeatEightArcadePricing).
      children: (
        <BeatEightArcadePricing
          scrollVh={scrollVh}
          segmentStartVh={480}
          segmentDurationVh={75}
        />
      ),
    },
    {
      id: 'beat-9-communities-text',
      startVh: 555,
      durationVh: 75,
      shellClassName: HERO_TEXT_PANEL_SHELL,
      zIndex: 5,
      tracks: {
        opacity: [
          [0, 0],
          [12, 1],
          [80, 1],
          [100, 0, 'easeOut'],
        ],
        yPx: [
          [0, 32],
          [12, 0],
          [80, 0],
          [100, -28, 'easeOut'],
        ],
      },
      children: (
        <HeroTextPanel
          title="Bring any group together"
          body="Games that connect communities off-stream, too. Play in the office, on the couch, or across timezones."
          palette="pink"
        />
      ),
    },
    {
      id: 'beat-9-polaroid-triptych',
      startVh: 555,
      durationVh: 75,
      shellClassName: BEAT_NINE_VISUAL_SHELL,
      zIndex: 4,
      // Each polaroid choreographs its own entrance/exit (see
      // BeatNineCommunities). The shell-level envelope holds 1 across the
      // beat then hard-cuts to 0 so the wrapper never traps pointer events
      // past the last polaroid's exit — same pattern as BeatSixPyramid.
      tracks: {
        opacity: [
          [0, 1],
          [96, 1],
          [96.5, 0],
        ],
      },
      children: (
        <BeatNineCommunities
          scrollVh={scrollVh}
          segmentStartVh={555}
          segmentDurationVh={75}
        />
      ),
    },
  ];

  useHomeColorSync(scrollVh, BACKDROP_SEGMENTS);

  return (
    <div className="h-[100svh] bg-neutral-950 text-white">
      <SEO />
      <main ref={pageRef} className="scroll-shell">
        <section
          ref={stageRef}
          className="scroll-shell__stage"
          aria-label="Home background color scroll"
          style={{ height: `${stageHeightVh}vh` }}
        >
          <div className="scroll-shell__sticky">
            <ScrollBackdrop
              scrollVh={scrollVh}
              segments={BACKDROP_SEGMENTS}
              behindSegments={
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{
                    opacity: 0.24,
                    filter: 'grayscale(1)',
                    pointerEvents: 'none',
                  }}
                >
                  <BalloonField decorative />
                </div>
              }
            />
            {AVATAR_SEGMENTS.map((avatarSegment) => (
              <ScrollAvatar
                key={avatarSegment.id}
                scrollVh={scrollVh}
                startVh={avatarSegment.startVh}
                durationVh={avatarSegment.durationVh}
                colorFamily={avatarSegment.colorFamily}
                eyeShape={avatarSegment.eyeShape}
                mouthShape={avatarSegment.mouthShape}
              />
            ))}
            {contentSegments.map((contentSegment) => (
              <ScrollContent
                key={contentSegment.id}
                scrollVh={scrollVh}
                startVh={contentSegment.startVh}
                durationVh={contentSegment.durationVh}
                shellClassName={contentSegment.shellClassName}
                tracks={contentSegment.tracks}
                zIndex={contentSegment.zIndex}
                style={contentSegment.style}
              >
                {contentSegment.children}
              </ScrollContent>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default Home;
